import React, {useEffect, useId, useState} from "react";
import {Badge, Box, Button, Divider, Drawer, IconButton, Stack, Tooltip, Typography, Alert} from "@mui/material";
import {CloseOutlined as Close, DoneAllOutlined as DoneAll, NotificationsOutlined as Notifications,
    VolumeOffOutlined, VolumeUpOutlined} from "@mui/icons-material";
import {useLocation, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useUser} from "../providers/user-context";
import {useNotifications} from "./notification-context";
import useNotificationFeed from "./use-notification-feed";
import NotificationFeed, {NotificationFilters} from "./notification-feed";

export default function Notification() {
    const center = useNotifications();
    return center?.enabled ? <NotificationPanel/> : null;
}

function NotificationPanel() {
    const center = useNotifications(), {tenantType} = useUser(), {t} = useTranslation();
    const [open, setOpen] = useState(false), titleId = useId();
    const location = useLocation(), navigate = useNavigate();
    const feed = useNotificationFeed({enabled: open});
    useEffect(() => {setOpen(false);}, [location.key]);
    const onOpen = row => {setOpen(false); center.openNotification(row);};
    const history = () => {
        setOpen(false);
        navigate(tenantType === "PHARMACY" ? "/pharmacy/all-notifications" : "/distributor/all-notifications");
    };
    return <>
        <Tooltip title={t(center.countError ? "NotificationCountUnavailable" : "Notifications")}>
            <IconButton color="inherit" onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}
                aria-label={center.countError || center.count == null ? t("Notifications") : t("NotificationUnreadCount", {count: center.count})}>
                <Badge badgeContent={center.count} color="error" max={99}
                    sx={{"& .MuiBadge-badge": {fontSize: "0.7rem", height: 16, minWidth: 16, px: "6px"}}}><Notifications/></Badge>
            </IconButton>
        </Tooltip>
        <Drawer anchor="right" open={open} onClose={() => setOpen(false)} sx={{zIndex: theme => theme.zIndex.modal}}
            PaperProps={{ref: open ? center.setFeedbackContainer : null, role: "dialog", "aria-modal": true, "aria-labelledby": titleId,
                sx: {width: {xs: "100%", sm: 420}, maxWidth: "100vw"}}}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px: 2.5, pt: 2, pb: 1, flexShrink: 0}}>
                <Typography id={titleId} variant="h6" fontWeight={600}>{t("Notifications")}</Typography>
                <Stack direction="row" spacing={0.5} sx={{flexShrink: 0}}>
                    <Tooltip title={t(center.soundEnabled ? "SoundOn" : "SoundOff")}>
                        <IconButton aria-label={t(center.soundEnabled ? "SoundOn" : "SoundOff")} aria-pressed={center.soundEnabled}
                            color={center.soundEnabled ? "primary" : "default"} onClick={center.toggleSound}>
                            {center.soundEnabled ? <VolumeUpOutlined/> : <VolumeOffOutlined/>}
                        </IconButton>
                    </Tooltip>
                    <IconButton autoFocus aria-label={t("Close")} onClick={() => setOpen(false)}><Close/></IconButton>
                </Stack>
            </Stack>
            <Box sx={{px: 2.5, pb: 1, flexShrink: 0}}>
                <Button size="small" startIcon={<DoneAll/>} disabled={center.markingAll || center.pendingIds.size > 0 || center.count === 0}
                    onClick={center.markAll}>{t("MarkAllAsRead")}</Button>
            </Box>
            <Box sx={{px: 2.5, flexShrink: 0}}><NotificationFilters feed={feed} fullWidth/></Box>
            <Divider/>
            <Box sx={{overflowY: "auto", flex: 1, minHeight: 0}}>
                {(center.connected === false || center.countError) && <Alert severity="info" sx={{m: 2}}
                    action={<Button color="inherit" onClick={() => {center.refreshCount(); feed.refresh();}}>{t("Retry")}</Button>}>
                    {t(center.countError ? "NotificationCountUnavailable" : "NotificationConnectionLost")}</Alert>}
                <NotificationFeed feed={feed} onOpen={onOpen}/>
            </Box>
            <Divider/>
            <Box sx={{p: 2, pb: "max(16px, env(safe-area-inset-bottom))", flexShrink: 0}}>
                <Button fullWidth variant="outlined" onClick={history}>{t("ViewAllNotifications")}</Button>
            </Box>
        </Drawer>
    </>;
}
